/**
 * Настройки → вкладка «Шаблоны смен» (manager design v3 §8.2).
 *
 * Список шаблонов смен карточками (название · диапазон времени mono ·
 * длительность/перерыв/отдел · цвет-маркер · активна/архив), кнопка
 * «+ Шаблон», форма создания/правки (название/время/отдел/цвет/перерыв),
 * удаление. Мобайл 375 — 1 колонка. RBAC: правка только управляющим.
 *
 * Бэк: /v1/scheduling/shift-templates (GET/POST/PUT/DELETE).
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { ShiftTemplate, ShiftTemplateInput, StaffDepartment } from '@hsc/types';
import { useStaffDepartments } from '../staff/useStaff.js';
import {
  useCreateShiftTemplate,
  useDeleteShiftTemplate,
  useShiftTemplates,
  useUpdateShiftTemplate,
} from '../schedules/useShiftTemplates.js';

// Палитра по дизайну: день=charcoal, ночь=slate-blue, полусмена=sage, спа=plum.
const COLOR_PRESETS: ReadonlyArray<{ hex: string; label: string }> = [
  { hex: '#4D4239', label: 'День (charcoal)' },
  { hex: '#3A4D5C', label: 'Ночь (slate)' },
  { hex: '#7C8A6B', label: 'Полусмена (sage)' },
  { hex: '#8A6F8E', label: 'Спа (plum)' },
  { hex: '#A6803C', label: 'Бар (amber)' },
];
const DEFAULT_COLOR = '#4D4239';

/** Длительность диапазона HH:MM–HH:MM в часах (через полночь — корректно). */
function toMinutes(hhmm: string): number {
  const parts = hhmm.split(':');
  const h = Number(parts[0] ?? 0);
  const m = Number(parts[1] ?? 0);
  return h * 60 + m;
}

function durationHours(start: string, end: string): number {
  let mins = toMinutes(end) - toMinutes(start);
  if (mins <= 0) mins += 24 * 60;
  return Math.round((mins / 60) * 10) / 10;
}

function deptName(departments: StaffDepartment[], id?: string): string {
  if (!id) return 'все отделы';
  return departments.find((d) => d.id === id)?.name ?? 'отдел';
}

function emptyDraft(): ShiftTemplateInput {
  return {
    name: '',
    start_time: '08:00',
    end_time: '16:00',
    department_id: undefined,
    color: DEFAULT_COLOR,
    break_minutes: 0,
    sort_order: 0,
  };
}

export function ShiftTemplatesPanel({ canManage }: { canManage: boolean }): JSX.Element {
  const templatesQuery = useShiftTemplates(true);
  const departments = useStaffDepartments().data ?? [];
  const [editing, setEditing] = useState<{ id?: string; draft: ShiftTemplateInput } | null>(null);

  const create = useCreateShiftTemplate();
  const update = useUpdateShiftTemplate();
  const remove = useDeleteShiftTemplate();
  const [formErr, setFormErr] = useState<string | null>(null);

  const templates = useMemo(() => templatesQuery.data ?? [], [templatesQuery.data]);

  const startCreate = (): void => {
    setFormErr(null);
    setEditing({ draft: emptyDraft() });
  };
  const startEdit = (t: ShiftTemplate): void => {
    setFormErr(null);
    setEditing({
      id: t.id,
      draft: {
        name: t.name,
        start_time: t.start_time,
        end_time: t.end_time,
        department_id: t.department_id,
        color: t.color,
        break_minutes: t.break_minutes,
        sort_order: t.sort_order,
      },
    });
  };

  const submit = (): void => {
    if (!editing) return;
    setFormErr(null);
    const onErr = (e: Error): void => setFormErr(e.message || 'Не удалось сохранить шаблон');
    if (editing.id) {
      update.mutate(
        { id: editing.id, input: editing.draft },
        { onSuccess: () => setEditing(null), onError: onErr },
      );
    } else {
      create.mutate(editing.draft, { onSuccess: () => setEditing(null), onError: onErr });
    }
  };

  const pending = create.isPending || update.isPending;

  if (templatesQuery.isLoading) {
    return (
      <section data-testid="shift-templates-panel">
        <div className="fleet-grid" data-testid="shift-templates-skeleton">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={120} radius={16} />
          ))}
        </div>
      </section>
    );
  }

  if (templatesQuery.isError) {
    return (
      <section data-testid="shift-templates-panel">
        <Card>
          <p role="alert">Не удалось загрузить шаблоны смен. Попробуйте обновить страницу.</p>
        </Card>
      </section>
    );
  }

  // Создание/правка шаблона — ОТДЕЛЬНЫЙ экран (форма вместо списка), не
  // расширение сверху. Возврат — кнопкой «Назад» снизу (feedback_every_form…).
  if (canManage && editing !== null) {
    return (
      <section data-testid="shift-templates-panel">
        <div className="ta-topbar">
          <h1 className="hsc-page__title">
            {editing.id ? 'Правка шаблона' : 'Новый шаблон смены'}
          </h1>
        </div>
        <Card>
          <form
            className="hsc-stack sched-addform"
            data-testid="shift-template-form"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <label className="sched-field">
              <span>Название</span>
              <input
                type="text"
                value={editing.draft.name}
                maxLength={128}
                placeholder="Утро ресепшн"
                onChange={(e) =>
                  setEditing((s) => (s ? { ...s, draft: { ...s.draft, name: e.target.value } } : s))
                }
                data-testid="tpl-name"
              />
            </label>

            <div className="sched-field-row">
              <label className="sched-field">
                <span>Начало</span>
                <input
                  type="time"
                  value={editing.draft.start_time}
                  onChange={(e) =>
                    setEditing((s) =>
                      s ? { ...s, draft: { ...s.draft, start_time: e.target.value } } : s,
                    )
                  }
                  data-testid="tpl-start"
                />
              </label>
              <label className="sched-field">
                <span>Конец</span>
                <input
                  type="time"
                  value={editing.draft.end_time}
                  onChange={(e) =>
                    setEditing((s) =>
                      s ? { ...s, draft: { ...s.draft, end_time: e.target.value } } : s,
                    )
                  }
                  data-testid="tpl-end"
                />
              </label>
              <label className="sched-field">
                <span>Перерыв, мин</span>
                <input
                  type="number"
                  min={0}
                  value={editing.draft.break_minutes ?? 0}
                  onChange={(e) =>
                    setEditing((s) =>
                      s
                        ? { ...s, draft: { ...s.draft, break_minutes: Number(e.target.value) || 0 } }
                        : s,
                    )
                  }
                  data-testid="tpl-break"
                />
              </label>
            </div>

            <label className="sched-field">
              <span>Отдел</span>
              <select
                value={editing.draft.department_id ?? ''}
                onChange={(e) =>
                  setEditing((s) =>
                    s
                      ? {
                          ...s,
                          draft: { ...s.draft, department_id: e.target.value || undefined },
                        }
                      : s,
                  )
                }
                data-testid="tpl-dept"
              >
                <option value="">Все отделы</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="sched-field">
              <span>Цвет</span>
              <div className="tpl-colors" data-testid="tpl-colors">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    className={`tpl-color${editing.draft.color === c.hex ? ' is-active' : ''}`}
                    style={{ background: c.hex }}
                    title={c.label}
                    aria-label={c.label}
                    aria-pressed={editing.draft.color === c.hex}
                    onClick={() =>
                      setEditing((s) => (s ? { ...s, draft: { ...s.draft, color: c.hex } } : s))
                    }
                    data-testid={`tpl-color-${c.hex.slice(1)}`}
                  />
                ))}
              </div>
            </div>

            {formErr && (
              <p className="fleet-card__err" role="alert" data-testid="tpl-error">
                {formErr}
              </p>
            )}

            <div className="sched-addform__actions">
              <Button type="submit" disabled={editing.draft.name.trim() === '' || pending} data-testid="tpl-submit">
                {pending ? 'Сохранение…' : editing.id ? 'Сохранить' : 'Создать'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditing(null)}
                data-testid="tpl-cancel"
              >
                Назад
              </Button>
            </div>
          </form>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="shift-templates-panel">
      <div className="sched-head">
        <p className="ta-muted" data-testid="shift-templates-count">
          Шаблоны смен · {templates.length}
        </p>
        {canManage && (
          <button
            type="button"
            className="fleet-tab is-active sched-add-btn"
            onClick={startCreate}
            data-testid="shift-template-add"
          >
            + Шаблон
          </button>
        )}
      </div>

      {templates.length === 0 ? (
        <Card>
          <p data-testid="shift-templates-empty">Создайте первый шаблон смены.</p>
        </Card>
      ) : (
        <ul className="fleet-grid" data-testid="shift-templates-grid">
          {templates.map((t) => (
            <li
              key={t.id}
              className={`fleet-card sched-tpl-card${t.is_active ? '' : ' sched-tpl-card--archived'}`}
              data-testid={`shift-template-${t.id}`}
            >
              <div className="fleet-card__head">
                <span className="fleet-card__title">
                  <i className="sched-tpl-dot" style={{ background: t.color }} />
                  {t.name}
                </span>
                <span className={`fleet-badge fleet-badge--${t.is_active ? 'ok' : 'muted'}`}>
                  {t.is_active ? 'активна' : 'архив'}
                </span>
              </div>
              <div className="sched-tpl-time">
                {t.start_time} — {t.end_time}
              </div>
              <div className="fleet-card__meta">
                <span>{durationHours(t.start_time, t.end_time)} ч</span>
                {t.break_minutes > 0 && <span>· перерыв {t.break_minutes} мин</span>}
                <span>· {deptName(departments, t.department_id)}</span>
              </div>
              {canManage && (
                <div className="fleet-card__actions sched-tpl-actions">
                  <button
                    type="button"
                    className="ta-back"
                    onClick={() => startEdit(t)}
                    data-testid={`shift-template-edit-${t.id}`}
                  >
                    Изменить
                  </button>
                  <button
                    type="button"
                    className="ta-back sched-tpl-del"
                    onClick={() => {
                      if (window.confirm(`Удалить шаблон «${t.name}»?`)) remove.mutate(t.id);
                    }}
                    data-testid={`shift-template-delete-${t.id}`}
                  >
                    Удалить
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
